import { createSlice } from "@reduxjs/toolkit";
import {
    ERLOESE_INTEGRATIONS,
    STANDARDMANDAT_TABS,
    ZAHLUNG_PROVIDERS,
} from "../../statics/StandardmandatCatalog";

export const initialStandardmandatState = {
    isLoading: false,
    isSaving: false,
    loaded: false,
    errorMessage: null,
    successMessage: null,
    warningMessage: null,

    // Chart of accounts + account length chosen by the StB; drives the SKR-based suggestions.
    skr: "SKR_04",
    accountNumberLength: "4",

    activeTab: STANDARDMANDAT_TABS.ERLOESE,
    activeErloese: ERLOESE_INTEGRATIONS[0].key,
    activeZahlung: ZAHLUNG_PROVIDERS[0].key,

    // defaults[integrationKey][fieldName] = { accountValue, buCode, startDate }
    defaults: {},
    // versions[integrationKey] = group version (echoed back on save for concurrent-edit detection)
    versions: {},
    // integrationKeys the user changed this session — only these are persisted on save, so untouched
    // integrations aren't re-PUT (which would bump their version and spuriously warn other sessions).
    dirty: [],
};

const markDirty = (state, integrationKey) => {
    if (!state.dirty.includes(integrationKey)) {
        state.dirty.push(integrationKey);
    }
};

const ensureIntegration = (state, integrationKey) => {
    if (!state.defaults[integrationKey]) {
        state.defaults[integrationKey] = {};
    }
    return state.defaults[integrationKey];
};

const standardmandatSlice = createSlice({
    name: "standardmandat",
    initialState: { ...initialStandardmandatState },
    reducers: {
        loadStandardmandatRequest(state) {
            state.isLoading = true;
            state.errorMessage = null;
            state.successMessage = null;
            state.warningMessage = null;
        },
        loadStandardmandatSuccess(state, action) {
            const defaults = {};
            const versions = {};
            (action.payload?.integrations || []).forEach((integration) => {
                const fields = {};
                (integration.accounts || []).forEach((account) => {
                    fields[account.fieldName] = {
                        accountValue: account.accountValue ?? "",
                        buCode: account.buCode ?? "",
                        startDate: account.startDate ?? null,
                    };
                });
                defaults[integration.integrationKey] = fields;
                versions[integration.integrationKey] = integration.version ?? 0;
            });
            state.defaults = defaults;
            state.versions = versions;
            state.dirty = [];
            state.isLoading = false;
            state.loaded = true;
        },
        loadStandardmandatFailure(state, action) {
            state.isLoading = false;
            state.errorMessage = action.payload;
        },

        saveIntegrationRequest(state) {
            state.isSaving = true;
            state.errorMessage = null;
            state.successMessage = null;
            state.warningMessage = null;
        },
        saveIntegrationSuccess(state, action) {
            const { integrationKey, integrationDefaults, warningMessage } = action.payload;
            const fields = {};
            (integrationDefaults?.accounts || []).forEach((account) => {
                fields[account.fieldName] = {
                    accountValue: account.accountValue ?? "",
                    buCode: account.buCode ?? "",
                    startDate: account.startDate ?? null,
                };
            });
            state.defaults[integrationKey] = fields;
            state.versions[integrationKey] = integrationDefaults?.version ?? 0;
            state.dirty = state.dirty.filter((key) => key !== integrationKey);
            state.isSaving = false;
            state.successMessage = "Einstellungen gespeichert.";
            state.warningMessage = warningMessage || null;
        },
        saveIntegrationFailure(state, action) {
            state.isSaving = false;
            state.errorMessage = action.payload;
        },

        setSkr(state, action) {
            state.skr = action.payload;
        },
        setAccountNumberLength(state, action) {
            state.accountNumberLength = action.payload;
        },
        setActiveTab(state, action) {
            state.activeTab = action.payload;
        },
        setActiveErloese(state, action) {
            state.activeErloese = action.payload;
        },
        setActiveZahlung(state, action) {
            state.activeZahlung = action.payload;
        },

        setFieldValue(state, action) {
            const { integrationKey, fieldName, field, value } = action.payload;
            const integration = ensureIntegration(state, integrationKey);
            if (!integration[fieldName]) {
                integration[fieldName] = { accountValue: "", buCode: "", startDate: null };
            }
            integration[fieldName][field] = value;
            markDirty(state, integrationKey);
        },

        // "Für alle übernehmen": copy the source integration's field values into every target.
        applyToAllIntegrations(state, action) {
            const { sourceIntegrationKey, targetKeys, fieldNames } = action.payload;
            const source = state.defaults[sourceIntegrationKey] || {};
            targetKeys.forEach((targetKey) => {
                const target = ensureIntegration(state, targetKey);
                fieldNames.forEach((fieldName) => {
                    const sourceValue = source[fieldName];
                    const hasContent =
                        sourceValue && (sourceValue.accountValue || sourceValue.buCode || sourceValue.startDate);
                    if (hasContent) {
                        target[fieldName] = { ...sourceValue };
                    }
                });
                markDirty(state, targetKey);
            });
        },

        clearMessages(state) {
            state.errorMessage = null;
            state.successMessage = null;
            state.warningMessage = null;
        },
    },
});

export default standardmandatSlice;
